/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, useContext } from "react"
import collectionService from "../services/collection"
import { retrieveItemImageFromHero } from "../components/utilities"
import { collectionFullSlugPrefixer } from "../helper/collectionFullSlugPrefixer"

import PageContext from "../components/page-context"
import { t } from "../i18n-utility"

const FILTER_TYPE_CATEGORY = "category"
const FILTER_TYPE_FORMAT = "format"

const useRemoteCollectionHandler = (options) => {
  const { translations, pageUid } = useContext(PageContext)
  const collectionServiceObj = new collectionService(
    Object.assign(options, { pageUid: pageUid })
  )

  const [totalCount, setTotalCount] = useState(null)
  const [collectionForPage, setCollectionForPage] = useState([])
  const [availableFilterOptions, setAvailableFilterOptions] = useState({})
  const [activePage, setActivePage] = useState(null)
  const [hasNextPage, setHasNextPage] = useState(false)

  const setSelectedFilterOptions = async (options) => {
    let activeFilterOptions = []
    if (options.formats.length) {
      activeFilterOptions.push({
        user_selected_formats: options.formats.join(","),
      })
    }
    if (options.root_categories.length) {
      activeFilterOptions.push({
        user_selected_root_categories: options.root_categories.join(","),
      })
    }
    collectionServiceObj.setUserFilterParams(activeFilterOptions)
    // start with page 1 when filter options are freshly set
    const newItems = await loadPage(1)
    setActivePage(1)
    setCollectionForPage(newItems)
  }

  const prepareAvailableFilterOptions = (filterableValues) => {
    let preparedAvailableFilterOptions = {}
    if(options.filter_type?.includes(FILTER_TYPE_FORMAT)) {
      if (filterableValues.formats && filterableValues.formats.length > 0) {
        preparedAvailableFilterOptions["formats"] = filterableValues.formats.map(
          (format) => {
            return {
              uid: format,
              title: t(translations, `filters.${format}`, true),
              disabled: false,
            }
          }
        )
      }
    }

    if(options.filter_type?.includes(FILTER_TYPE_CATEGORY)) {
      if (
        filterableValues.root_categories &&
        filterableValues.root_categories.length > 0
      ) {
        preparedAvailableFilterOptions["root_categories"] =
          filterableValues.root_categories.map((cat) => {
            cat.disabled = false
            return cat
          })
      }
    }
    setAvailableFilterOptions(preparedAvailableFilterOptions)
  }

  const disableEmptyResultFilterOptions = (subsequentFilterableValues) => {
    const userSelected = collectionServiceObj.userFilterParams.map((p) => {
      return Object.keys(p)[0]
    })
//    console.debug("disableEmptyResultFilterOptions -> availableFilterOptions", availableFilterOptions)
//    console.debug("disableEmptyResultFilterOptions -> subsequentFilterableValues", subsequentFilterableValues)
//    console.debug("disableEmptyResultFilterOptions -> userselected", userSelected)
    if (
      userSelected.length === 1 &&
      userSelected.indexOf("user_selected_formats") !== -1
    ) {
      let enabledRootCategoryUids =
        subsequentFilterableValues.root_categories.map((c) => {
          return c.uid
        })

      let filterOptionsCopy = availableFilterOptions
      if(availableFilterOptions && availableFilterOptions.root_categories) {
        filterOptionsCopy.root_categories = filterOptionsCopy.root_categories.map(
        (c) => {
          if (enabledRootCategoryUids.indexOf(c.uid) !== -1) {
            c.disabled = false
          } else {
            c.disabled = true
          }
          return c
        })
      }  
      setAvailableFilterOptions(filterOptionsCopy)
    } else if (
      userSelected.length === 1 &&
      userSelected.indexOf("user_selected_root_categories") !== -1
    ) {
      let enabledFormats = subsequentFilterableValues.formats
      let filterOptionsCopy = availableFilterOptions
      if(availableFilterOptions && availableFilterOptions.formats) {
        filterOptionsCopy.formats = filterOptionsCopy.formats.map((c) => {
          if (enabledFormats.indexOf(c.uid) !== -1) {
            c.disabled = false
          } else {
            c.disabled = true
          }
          return c
        })
      }
      setAvailableFilterOptions(filterOptionsCopy)
    }
  }

  const loadPage = async (page) => {
    try {
      let collectionResult =
        await collectionServiceObj.getCollectionForSinglePage(page)
      collectionResult = retrieveItemImageFromHero(collectionResult)
      collectionResult = collectionFullSlugPrefixer(collectionResult)
      setHasNextPage(collectionServiceObj.lastRequestMeta.has_next_page)
      setTotalCount(collectionServiceObj.lastRequestMeta.total)

      if (page === 1 && collectionServiceObj.filterableValues) {
        prepareAvailableFilterOptions(collectionServiceObj.filterableValues)
      }

      if (collectionServiceObj.subsequentFilterableValues) {
        disableEmptyResultFilterOptions(
          collectionServiceObj.subsequentFilterableValues
        )
      }

      return collectionResult
    } catch (err) {
      setTotalCount(undefined)
      console.error(err)
      return []
    }
  }

  const addItems = (newItems) => {
    if (collectionForPage.length > 0) {
      setCollectionForPage(collectionForPage.concat(newItems))
    }
  }

  const handleNextPage = async () => {
    const nextPage = activePage + 1
    const newItems = await loadPage(nextPage)
    setActivePage(nextPage)
    addItems(newItems)
  }

  const handleFirstPage = async () => {
    const newItems = await loadPage(1)
    setActivePage(1)
    setCollectionForPage(newItems)
  }

  useEffect(() => {
    if (
      collectionForPage &&
      collectionForPage.length &&
      window.upgradeAllBehaviours
    ) {
      // ensure grid and images are initialized by ui kit
      window.upgradeAllBehaviours()
    }
  }, [collectionForPage])

  // init
  useEffect(() => {
    handleFirstPage()
  }, [])

  return {
    collectionForPage,
    activePage,
    hasNextPage,
    totalCount,
    availableFilterOptions,
    setSelectedFilterOptions,
    handleNextPage,
  }
}

export default useRemoteCollectionHandler
